"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, Compass, X, Zap } from "lucide-react";

import { IntegrationBrandIcon } from "@/components/site/primitives/integration-brand-icon";
import { OwnerAvatar } from "@/components/site/sections/overview-board/owner-badge";
import {
  LANE_STYLES,
  OVERVIEW_EASE,
  SECTION_STYLES,
  SIGNAL_SOURCES,
  SIGNAL_SOURCE_ORDER,
  SYNC_PROVIDER_LABELS,
  prefersReducedMotion,
} from "@/components/site/sections/overview-board/styles";
import { formatWhyThisOwner } from "@/components/site/sections/six-pillars-preview/charts-and-badges";
import {
  FloatingBoundaryContext,
  MentionText,
} from "@/components/site/sections/six-pillars-preview/shared-surface";
import {
  allInsights,
  allSteps,
  findItem,
  isStep,
  isViewerItem,
  laneDotMap,
  linkedItems,
  neighbourId,
  positionOf,
  windowKeyDelta,
  type OverviewDocument,
  type OverviewItem,
} from "@/lib/overview-document";
import { getSixPillarsPreviewPerson } from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

const iconButton =
  "inline-flex size-8 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none disabled:opacity-35";

/**
 * One step or insight in a dialog: path and paging on top, the item and its
 * signals on the left, owner and plan context on the right. Arrow keys page
 * through every insight or every step; Escape closes only this window.
 */
export function OverviewItemWindow({
  document,
  itemId,
  onNavigate,
  onClose,
  onRestoreFocus,
}: {
  document: OverviewDocument;
  itemId: string | null;
  onNavigate: (id: string) => void;
  onClose: () => void;
  onRestoreFocus: () => void;
}) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  // Mention cards anchor inside the window, above the page and any fullscreen preview.
  const boundary = React.useMemo(() => ({ boundaryRef: contentRef }), []);

  return (
    <DialogPrimitive.Root
      open={itemId !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="overview-window-scrim fixed inset-0 z-[80] bg-white/72 backdrop-blur-md" />
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          tabIndex={-1}
          onEscapeKeyDown={(event) => {
            // The fullscreen preview listens for Escape on window; only this window should close.
            event.stopPropagation();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onRestoreFocus();
          }}
          onKeyDown={(event) => {
            const delta = windowKeyDelta(event.key);
            const next = itemId && delta !== 0 ? neighbourId(document, itemId, delta) : null;

            if (next) {
              event.preventDefault();
              onNavigate(next);
            }
          }}
          className="overview-window @container fixed inset-0 z-[81] flex h-dvh w-screen flex-col overflow-hidden bg-background text-foreground outline-none sm:inset-auto sm:top-[4vh] sm:left-1/2 sm:h-auto sm:max-h-[92vh] sm:w-[calc(100%-2rem)] sm:max-w-[66rem] sm:-translate-x-1/2 sm:rounded-[1.5rem] sm:border sm:border-border/70 sm:shadow-[0_40px_120px_-44px_rgba(15,23,42,0.4)]"
        >
          <FloatingBoundaryContext.Provider value={boundary}>
            {itemId ? (
              <ItemWindowContent
                document={document}
                itemId={itemId}
                onNavigate={onNavigate}
                contentRef={contentRef}
              />
            ) : null}
          </FloatingBoundaryContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ItemWindowContent({
  document,
  itemId,
  onNavigate,
  contentRef,
}: {
  document: OverviewDocument;
  itemId: string;
  onNavigate: (id: string) => void;
  contentRef: React.RefObject<HTMLDivElement | null>;
}) {
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const shownRef = React.useRef<string | null>(null);
  const item = findItem(document, itemId);
  const position = positionOf(document, itemId);

  // Paging keeps the shell still and moves only the content, in the paging direction.
  React.useLayoutEffect(() => {
    const body = bodyRef.current;
    const previous = shownRef.current;
    shownRef.current = itemId;

    if (!body || previous === null || previous === itemId) {
      return;
    }

    body.scrollTop = 0;

    // A paging button that just became disabled would drop focus to the page.
    const active = globalThis.document.activeElement;
    if (active instanceof HTMLButtonElement && active.disabled) {
      contentRef.current?.focus();
    }

    if (typeof body.animate !== "function") {
      return;
    }

    const order = [...allInsights(document), ...allSteps(document)].map(
      (entry) => entry.id,
    );
    const direction = order.indexOf(itemId) < order.indexOf(previous) ? -1 : 1;
    body.animate(
      prefersReducedMotion()
        ? [{ opacity: 0.4 }, { opacity: 1 }]
        : [
            { opacity: 0, transform: `translateY(${direction * 14}px)` },
            { opacity: 1, transform: "none" },
          ],
      { duration: 320, easing: OVERVIEW_EASE },
    );
  }, [document, itemId, contentRef]);

  if (!item || !position) {
    return null;
  }

  const step = isStep(item);
  const previousId = neighbourId(document, itemId, -1);
  const nextId = neighbourId(document, itemId, 1);
  const mine = isViewerItem(document, item);
  const owner = getSixPillarsPreviewPerson(item.suggestedOwner);
  const typeLabel = step ? "Step" : SECTION_STYLES[item.section].singular;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-12 flex-none items-center justify-between gap-2 border-b pr-2 pl-4 sm:gap-3 sm:pr-2.5 sm:pl-5">
        <nav
          aria-label="Location"
          className="flex min-w-0 items-center gap-1.5 text-[0.8125rem] text-muted-foreground"
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-2 flex-none rounded-full",
              step
                ? LANE_STYLES[item.lane].dotClassName
                : SECTION_STYLES[item.section].dotClassName,
            )}
          />
          {/* On the narrowest phones the horizon or type alone names the place. */}
          <span className="truncate @max-[22rem]:sr-only">{step ? "Roadmap" : "Overview"}</span>
          <span aria-hidden="true" className="text-foreground/25 @max-[22rem]:hidden">
            /
          </span>
          <span className="truncate font-medium text-foreground">
            {step ? LANE_STYLES[item.lane].label : SECTION_STYLES[item.section].label}
          </span>
        </nav>
        <div className="flex flex-none items-center gap-0.5">
          <span className="mr-1 text-xs whitespace-nowrap text-muted-foreground tabular-nums sm:mr-1.5">
            {position.position} of {position.total}
          </span>
          <button
            type="button"
            aria-label={step ? "Previous step" : "Previous insight"}
            disabled={previousId === null}
            onClick={() => previousId && onNavigate(previousId)}
            className={iconButton}
          >
            <ChevronUp className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={step ? "Next step" : "Next insight"}
            disabled={nextId === null}
            onClick={() => nextId && onNavigate(nextId)}
            className={iconButton}
          >
            <ChevronDown className="size-4" aria-hidden="true" />
          </button>
          <span aria-hidden="true" className="mx-1 h-[18px] w-px bg-border sm:mx-1.5" />
          <DialogPrimitive.Close aria-label="Close" className={iconButton}>
            <X className="size-4" aria-hidden="true" />
          </DialogPrimitive.Close>
        </div>
      </div>

      <div
        ref={bodyRef}
        className="grid min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-color:var(--border)_transparent] [scrollbar-width:thin] @min-[48rem]:grid-cols-[minmax(0,1fr)_minmax(16rem,20rem)]"
      >
        <div className="flex min-w-0 flex-col gap-6 px-5 pt-6 pb-7 sm:px-9 sm:pt-8 sm:pb-9">
          <div className="flex flex-col gap-3.5">
            <DialogPrimitive.Title className="text-[1.375rem] leading-[1.22] font-semibold tracking-[-0.026em] text-balance sm:text-[1.75rem]">
              {item.title}
            </DialogPrimitive.Title>
            <MentionText
              text={item.whyItMatters}
              className="max-w-[64ch] text-base leading-[1.7] text-foreground/80"
            />
          </div>

          {step ? (
            <div className="flex gap-3.5 rounded-[14px] border px-5 py-[1.125rem]">
              <span
                aria-hidden="true"
                className="mt-0.5 size-5 flex-none rounded-[6px] border-2 border-foreground"
              />
              <span className="flex flex-col gap-1">
                <span className="text-[0.8125rem] font-semibold">Done when</span>
                <span className="text-[0.9375rem] leading-relaxed text-foreground/90">
                  {item.doneWhen}
                </span>
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-3">
              <div className="flex flex-col gap-2 rounded-[14px] bg-foreground px-5 py-[1.125rem] text-background">
                <span className="inline-flex items-center gap-2 text-[0.8125rem] font-semibold">
                  <Zap className="size-3.5" aria-hidden="true" />
                  Quick win
                </span>
                <span className="text-sm leading-relaxed text-background/85">
                  {item.quickWin}
                </span>
              </div>
              <div className="flex flex-col gap-2 rounded-[14px] border px-5 py-[1.125rem]">
                <span className="inline-flex items-center gap-2 text-[0.8125rem] font-semibold">
                  <Compass className="size-3.5" aria-hidden="true" />
                  Long-term move
                </span>
                <span className="text-sm leading-relaxed text-foreground/80">
                  {item.longTermMove}
                </span>
              </div>
            </div>
          )}

          <ItemSignals item={item} />
        </div>

        <aside
          aria-label="Context"
          className="flex min-w-0 flex-col gap-7 border-t bg-muted/30 px-5 pt-6 pb-8 sm:px-7 @min-[48rem]:border-t-0 @min-[48rem]:border-l @min-[48rem]:pt-8"
        >
          <div className="flex flex-col gap-3">
            <span className="text-xs font-semibold text-muted-foreground">
              Suggested owner
            </span>
            <div className="flex items-center gap-3">
              <OwnerAvatar name={item.suggestedOwner} size="lg" mine={mine} />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5 text-[0.9375rem] font-semibold">
                  {item.suggestedOwner}
                  {mine ? (
                    <span className="rounded-full bg-foreground px-1.5 text-[0.6875rem] font-semibold text-background">
                      you
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-muted-foreground">
                  {owner ? `${owner.role} · ${document.teamName}` : document.teamName}
                </span>
              </span>
            </div>
            <p className="text-[0.8125rem] leading-[1.65] text-foreground/70">
              {formatWhyThisOwner(item.whyThisOwner, item.suggestedOwner)}
            </p>
          </div>

          <ItemWhereMap document={document} item={item} onNavigate={onNavigate} />
          <ItemLinks document={document} item={item} onNavigate={onNavigate} />
        </aside>
      </div>

      <div className="flex min-h-12 flex-none items-center gap-2.5 border-t px-5 py-2 text-xs text-muted-foreground">
        {step ? (
          <>
            <span className="inline-flex size-6 flex-none items-center justify-center rounded-full border bg-background [&_img]:size-3.5">
              <IntegrationBrandIcon slug={document.sync.lastSync.provider} size="sm" />
            </span>
            <span>
              Roadmap synced to {SYNC_PROVIDER_LABELS[document.sync.lastSync.provider]} ·{" "}
              <span className="font-medium whitespace-nowrap text-foreground/90">
                {document.sync.lastSync.at}
              </span>
            </span>
          </>
        ) : (
          <span>
            From the overview of{" "}
            <span className="font-medium text-foreground/90">{document.generatedOn}</span>
          </span>
        )}
      </div>

      <p aria-live="polite" className="sr-only">
        {typeLabel} {position.position} of {position.total}: {item.title}
      </p>
    </div>
  );
}

/** Supporting signals as one bordered list grouped by source. */
function ItemSignals({ item }: { item: OverviewItem }) {
  const sources = SIGNAL_SOURCE_ORDER.filter((key) => item.signals[key].length > 0);

  return (
    <div className="flex flex-col gap-3.5">
      <h3 className="text-base font-semibold tracking-[-0.01em]">
        Supporting signals{" "}
        <span className="font-medium text-muted-foreground/80 tabular-nums">
          {item.signalCount}
        </span>
      </h3>
      {!isStep(item) && item.evidence ? (
        <MentionText
          text={item.evidence}
          className="rounded-xl bg-muted/70 px-[1.125rem] py-4 text-sm leading-[1.65] text-foreground/90"
        />
      ) : null}
      <ul className="divide-y rounded-[14px] border">
        {sources.map((key) => {
          const { icon: Icon, label } = SIGNAL_SOURCES[key];

          return (
            <li key={key} className="flex flex-col gap-2.5 px-[1.125rem] pt-3.5 pb-4">
              <span className="flex items-center gap-2 text-[0.8125rem] font-semibold">
                <span className="inline-flex size-[26px] items-center justify-center rounded-lg bg-muted text-foreground/80">
                  <Icon className="size-3.5" aria-hidden="true" />
                </span>
                {label}
                <span className="ml-auto font-medium text-muted-foreground/80 tabular-nums">
                  {item.signals[key].length}
                </span>
              </span>
              <ul className="flex flex-col gap-2 pl-[34px]">
                {item.signals[key].map((signal) => (
                  <li
                    key={signal}
                    className="flex gap-2.5 text-[0.84375rem] leading-[1.55] text-foreground/90"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 size-[5px] flex-none rounded-full bg-foreground/35"
                    />
                    <MentionText text={signal} className="min-w-0" />
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * "Where it sits in the plan": the four horizons drawn like the board's lane
 * headers with every step as a dot. A step lights its own horizon and marks
 * itself; an insight fills the steps that address it.
 */
function ItemWhereMap({
  document,
  item,
  onNavigate,
}: {
  document: OverviewDocument;
  item: OverviewItem;
  onNavigate: (id: string) => void;
}) {
  const columns = laneDotMap(document, item.id);
  const unplanned = !isStep(item) && item.stepIds.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-semibold text-muted-foreground">
        Where it sits in the plan
      </span>
      {/* One grid for all columns: every rule and dot row starts on the same line. */}
      <div className="grid grid-flow-col grid-cols-4 grid-rows-[auto_auto_auto] gap-x-1.5 rounded-xl border bg-background px-3 pt-3 pb-3.5">
        {columns.map((column) => [
          <span
            key={`${column.lane}-label`}
            className={cn(
              "-ml-1.5 self-end justify-self-start rounded-full border px-1.5 py-px text-[0.6875rem] leading-tight transition-colors duration-300",
              column.current
                ? cn("font-semibold", LANE_STYLES[column.lane].badgeClassName)
                : "border-transparent text-muted-foreground",
            )}
          >
            {LANE_STYLES[column.lane].shortLabel}
          </span>,
          <span
            key={`${column.lane}-rule`}
            aria-hidden="true"
            className={cn(
              "mt-2 border-t-2 transition-opacity duration-300",
              LANE_STYLES[column.lane].ruleClassName,
              !column.current && "opacity-30",
            )}
          />,
          <span
            key={`${column.lane}-dots`}
            className="mt-1.5 -ml-1 flex min-h-[22px] flex-wrap content-start gap-0.5"
          >
            {column.dots.map((dot) => (
              <button
                key={dot.id}
                type="button"
                aria-label={`Step ${dot.position}: ${dot.title}${dot.linked ? ". Addresses this insight." : ""}`}
                aria-current={dot.current ? "step" : undefined}
                onClick={() => onNavigate(dot.id)}
                className="group inline-flex size-[22px] items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <span
                  className={cn(
                    "size-2.5 rounded-full transition-[transform,background-color,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-125 motion-reduce:transition-none",
                    dot.current
                      ? "bg-foreground ring-[1.5px] ring-foreground ring-offset-2 ring-offset-background"
                      : dot.linked
                        ? "bg-foreground"
                        : "bg-foreground/20",
                  )}
                />
              </button>
            ))}
          </span>,
        ])}
      </div>
      {unplanned ? (
        <span className="text-xs leading-snug text-muted-foreground">
          No roadmap step for this yet.
        </span>
      ) : null}
    </div>
  );
}

/** Step → the insights it addresses; insight → its roadmap steps. */
function ItemLinks({
  document,
  item,
  onNavigate,
}: {
  document: OverviewDocument;
  item: OverviewItem;
  onNavigate: (id: string) => void;
}) {
  const links = linkedItems(document, item.id);

  if (links.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-muted-foreground">
        {isStep(item) ? "Addresses" : "Roadmap steps"}
      </span>
      <ul className="-mx-2.5 flex flex-col">
        {links.map((linked) => (
          <li key={linked.id}>
            <button
              type="button"
              onClick={() => onNavigate(linked.id)}
              className={cn(
                "flex w-full items-start rounded-[10px] p-2.5 text-left transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                // Horizon labels differ in width, so they sit above the title and every title starts at the same edge.
                isStep(linked) ? "flex-col gap-1.5" : "gap-2.5",
              )}
            >
              {isStep(linked) ? (
                <span
                  className={cn(
                    "rounded-full border px-[7px] py-px text-[0.6875rem] font-semibold whitespace-nowrap",
                    LANE_STYLES[linked.lane].badgeClassName,
                  )}
                >
                  {LANE_STYLES[linked.lane].shortLabel}
                </span>
              ) : (
                <LinkedInsightIcon section={linked.section} />
              )}
              <span className="text-[0.8125rem] leading-[1.45] font-medium">
                {linked.title}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LinkedInsightIcon({
  section,
}: {
  section: keyof typeof SECTION_STYLES;
}) {
  const style = SECTION_STYLES[section];
  const Icon = style.icon;

  return (
    <span
      className={cn(
        "inline-flex size-[22px] flex-none items-center justify-center rounded-full border",
        style.badgeClassName,
      )}
    >
      <Icon className={cn("size-3", style.iconClassName)} aria-hidden="true" />
      <span className="sr-only">{style.singular}: </span>
    </span>
  );
}
